/**
 * Internationalization (i18n) System for Pindai Chat Widget
 * Supports Indonesian (id) and English (en) locales
 */

export const translations = {
  en: {
    // Widget UI
    title: 'Pindai Agent',
    placeholder: 'Write a message...',
    initialMessage: 'Hello! How can I help you today?',
    send: 'Send',
    close: 'Close',
    upload: 'Upload file',
    removeFile: 'Remove file',

    // Loading and status
    typingIndicator: 'AI is typing...',
    sending: 'Sending...',
    justNow: 'Just now',
    minutesAgo: '{minutes}m ago',

    // Offline/Online status
    offline: 'Offline - messages will be sent when online',
    connectionRestored: 'Connection restored',
    connectionLost: 'No internet connection',

    // Error messages
    errorGeneric: 'An error occurred. Please try again.',
    errorTimeout: 'Request timeout. Please try again.',
    errorNetwork: 'No internet connection. Check your network.',
    errorServer: 'Server is busy. Please try again later.',
    errorRateLimit: 'Too many messages. Please wait {seconds} seconds.',
    errorInvalidResponse: 'Invalid server response. Please contact support.',

    // File upload errors
    fileTypeNotSupported: 'File type not supported: {filename}',
    fileTooLarge: 'File too large: {filename} (max {maxSize}MB)',
    maxFilesExceeded: 'Maximum {maxFiles} files allowed',

    // Quick replies (default suggestions)
    quickReply1: 'How can I extract data from documents?',
    quickReply2: 'What file types are supported?',
    quickReply3: 'Tell me about pricing',
    quickReply4: 'Contact support',

    // Accessibility labels
    ariaOpenChat: 'Open chat widget',
    ariaCloseChat: 'Close chat window',
    ariaSendMessage: 'Send message',
    ariaMessageInput: 'Type your message',
    ariaUploadFile: 'Upload file',
    ariaRemoveFile: 'Remove file',
    ariaChatWindow: 'Chat window',
    ariaMessageLog: 'Chat messages',

    // New v3.0 keys
    bubbleDismiss: 'Dismiss',
    streamingError: 'Connection interrupted. Please try again.',
    humanAgentJoined: 'A team member has joined the conversation.',
    humanAgentLabel: 'Support Agent',
    actionIndicator: 'Performing action...',
    thinkingIndicator: 'Thinking...',
    poweredBy: 'Powered by',
    waitingForAgent: 'Waiting for a human agent...',
  },

  id: {
    // Widget UI
    title: 'Pindai Agent',
    placeholder: 'Tulis pesan...',
    initialMessage: 'Halo! Bagaimana saya bisa membantu Anda hari ini?',
    send: 'Kirim',
    close: 'Tutup',
    upload: 'Unggah file',
    removeFile: 'Hapus file',

    // Loading and status
    typingIndicator: 'AI sedang mengetik...',
    sending: 'Mengirim...',
    justNow: 'Baru saja',
    minutesAgo: '{minutes}m yang lalu',

    // Offline/Online status
    offline: 'Offline - pesan akan dikirim saat online',
    connectionRestored: 'Koneksi kembali',
    connectionLost: 'Tidak ada koneksi internet',

    // Error messages
    errorGeneric: 'Terjadi kesalahan. Silakan coba lagi.',
    errorTimeout: 'Waktu permintaan habis. Silakan coba lagi.',
    errorNetwork: 'Tidak ada koneksi internet. Periksa jaringan Anda.',
    errorServer: 'Server sedang sibuk. Silakan coba lagi dalam beberapa saat.',
    errorRateLimit: 'Terlalu banyak pesan. Silakan tunggu {seconds} detik.',
    errorInvalidResponse: 'Respons server tidak valid. Silakan hubungi dukungan.',

    // File upload errors
    fileTypeNotSupported: 'Jenis file tidak didukung: {filename}',
    fileTooLarge: 'File terlalu besar: {filename} (maks {maxSize}MB)',
    maxFilesExceeded: 'Maksimal {maxFiles} file diperbolehkan',

    // Quick replies (default suggestions)
    quickReply1: 'Bagaimana cara ekstraksi dokumen?',
    quickReply2: 'Jenis file apa yang didukung?',
    quickReply3: 'Tentang harga',
    quickReply4: 'Hubungi dukungan',

    // Accessibility labels
    ariaOpenChat: 'Buka widget chat',
    ariaCloseChat: 'Tutup jendela chat',
    ariaSendMessage: 'Kirim pesan',
    ariaMessageInput: 'Ketik pesan Anda',
    ariaUploadFile: 'Unggah file',
    ariaRemoveFile: 'Hapus file',
    ariaChatWindow: 'Jendela chat',
    ariaMessageLog: 'Pesan chat',

    // New v3.0 keys
    bubbleDismiss: 'Tutup',
    streamingError: 'Koneksi terputus. Silakan coba lagi.',
    humanAgentJoined: 'Anggota tim telah bergabung dalam percakapan.',
    humanAgentLabel: 'Agen Dukungan',
    actionIndicator: 'Sedang memproses...',
    thinkingIndicator: 'Sedang berpikir...',
    poweredBy: 'Dibuat dengan',
    waitingForAgent: 'Menunggu agen manusia...',
  }
};

/**
 * I18n class for managing translations
 */
export class I18n {
  constructor(locale = 'id') {
    this.locale = this.isValidLocale(locale) ? locale : 'id';
  }

  /**
   * Check if locale is valid
   */
  isValidLocale(locale) {
    return Object.keys(translations).includes(locale);
  }

  /**
   * Translate a key with optional parameter substitution
   * @param {string} key - Translation key
   * @param {object} params - Parameters to substitute in the translation
   * @returns {string} Translated string
   */
  t(key, params = {}) {
    let text = translations[this.locale]?.[key] || translations.en[key] || key;

    // Replace parameters like {param} with actual values
    Object.keys(params).forEach(param => {
      const regex = new RegExp(`\\{${param}\\}`, 'g');
      text = text.replace(regex, params[param]);
    });

    return text;
  }

  /**
   * Change the current locale
   * @param {string} locale - New locale (id or en)
   */
  setLocale(locale) {
    if (this.isValidLocale(locale)) {
      this.locale = locale;
      return true;
    }
    console.warn(`Invalid locale: ${locale}. Keeping current locale: ${this.locale}`);
    return false;
  }

  /**
   * Get current locale
   * @returns {string} Current locale
   */
  getLocale() {
    return this.locale;
  }

  /**
   * Get all available locales
   * @returns {string[]} Array of available locale codes
   */
  getAvailableLocales() {
    return Object.keys(translations);
  }
}

// Export default instance for convenience
export default I18n;
